/**
 * fuzzy.js — one-stop tolerant string matching used by every search & picker.
 *
 * Design goals:
 *   1. "vtrend" must match "V TREND" (ignore case + whitespace + punctuation).
 *   2. "vtrned" (one typo) must still match "V TREND".
 *   3. Cheap enough to run on every keystroke against a few thousand rows.
 */

// Normalise: lowercase, drop everything except a-z0-9. Keeps the hot path
// allocation-free enough to run inside a useMemo/filter loop.
function norm(s) {
    return (s == null ? "" : String(s)).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

// Bounded Damerau–Levenshtein — stops early once the running distance
// exceeds `max`. That's why it's O(needle × max) not O(needle × haystack).
function editDistance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const la = a.length, lb = b.length;
    if (la === 0) return lb;
    if (lb === 0) return la;
    let prev = new Array(lb + 1);
    for (let j = 0; j <= lb; j++) prev[j] = j;
    for (let i = 1; i <= la; i++) {
        const curr = new Array(lb + 1);
        curr[0] = i;
        let rowMin = curr[0];
        for (let j = 1; j <= lb; j++) {
            const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
            let d = Math.min(
                curr[j - 1] + 1,        // insertion
                prev[j] + 1,            // deletion
                prev[j - 1] + cost,     // substitution
            );
            if (i > 1 && j > 1 &&
                a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
                a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
                d = Math.min(d, prev[j - 2] !== undefined ? prev[j - 2] + 1 : d);
            }
            curr[j] = d;
            if (d < rowMin) rowMin = d;
        }
        if (rowMin > max) return max + 1;  // early exit
        prev = curr;
    }
    return prev[lb];
}

// True if `needle` characters appear IN ORDER inside `haystack` (allowing
// gaps) AND land within a compact span (≤ 3x the needle length). This catches
// abbreviations like "stmt" → "statement" without matching random letters
// scattered across a long label.
function isCompactSubsequence(needle, haystack) {
    let i = 0;
    let firstIdx = -1;
    let lastIdx = -1;
    for (let j = 0; j < haystack.length && i < needle.length; j++) {
        if (needle[i] === haystack[j]) {
            if (firstIdx < 0) firstIdx = j;
            lastIdx = j;
            i++;
        }
    }
    if (i !== needle.length) return false;
    const span = lastIdx - firstIdx + 1;
    return span <= Math.max(needle.length * 2, needle.length + 3);
}

/**
 * Returns true if `needle` should match `haystack`.
 * Uses three tiers, cheapest first:
 *   1. substring on normalized strings   (handles case + whitespace + punctuation)
 *   2. subsequence match                 (handles abbreviations, missing letters)
 *   3. bounded edit distance             (handles one/two-char typos)
 */
export function fuzzyMatch(needle, haystack) {
    const n = norm(needle);
    if (!n) return true;
    const h = norm(haystack);
    if (!h) return false;
    if (h.includes(n)) return true;
    if (n.length >= 3 && isCompactSubsequence(n, h)) return true;
    // Allow 1 typo for short queries, 2 for longer ones.
    const maxEdits = n.length <= 4 ? 1 : (n.length <= 10 ? 2 : 3);
    // Compare the needle against each word inside haystack so a 1-letter
    // typo in a single word doesn't need to cover the entire label.
    if (editDistance(n, h, maxEdits) <= maxEdits) return true;
    // Also compare needle against every whitespace/word-token in the
    // original haystack so "vtrned" catches "V TREND" via the "vtrend" token.
    const words = String(haystack || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    for (const w of words) {
        if (editDistance(n, w, maxEdits) <= maxEdits) return true;
    }
    return false;
}

/**
 * Filter a list of items using fuzzy matching against one or more string keys.
 *
 *   fuzzyFilter(vendors, "vtrend", ["name", "gstin"])
 *
 * If `keys` is omitted, every string value of each item is searched.
 */
export function fuzzyFilter(items, query, keys) {
    const q = String(query || "").trim();
    if (!q) return items;
    return (items || []).filter((it) => {
        if (keys && keys.length) {
            for (const k of keys) {
                if (fuzzyMatch(q, it?.[k])) return true;
            }
            return false;
        }
        for (const v of Object.values(it || {})) {
            if (typeof v === "string" && fuzzyMatch(q, v)) return true;
        }
        return false;
    });
}
